/**
 * Grafana Faro initialization and user context. Only initializes when
 * REACT_APP_OBSERVABILITY_ENABLED === 'true' and REACT_APP_FARO_COLLECT_URL is set.
 * When disabled, no Faro telemetry is sent; LogRocket (if REACT_APP_LOGROCKET_APP_ID is set) runs independently.
 *
 * Collect URL must be reachable and allow the app origin (CORS). If the app runs on
 * a different origin (e.g. app on localhost:3001, collect URL localhost:3000), the
 * collector must send Access-Control-Allow-Origin for the app origin or requests
 * will fail with "Provisional headers" / blocked response. For local dev, either
 * disable observability or point to a collector that allows your app origin.
 */

import {
  getWebInstrumentations,
  initializeFaro,
  getInternalFaroFromGlobalObject,
} from '@grafana/faro-web-sdk';
import { getObservabilityContext } from './init';
import type { FaroUserContext } from './types';

const enabled =
  process.env.REACT_APP_OBSERVABILITY_ENABLED === 'true' &&
  Boolean(process.env.REACT_APP_FARO_COLLECT_URL);
const collectUrl = process.env.REACT_APP_FARO_COLLECT_URL || '';
const appVersion = process.env.REACT_APP_VERSION ?? '0.0.0';
const environment = process.env.NODE_ENV ?? process.env.REACT_APP_ENV ?? 'production';

/**
 * Application identifier for filtering logs (e.g. game vs admin). Set REACT_APP_APP_NAME per app.
 */
const appName = process.env.REACT_APP_APP_NAME ?? 'safety-village-game-app';

let faroInstance: ReturnType<typeof initializeFaro> | undefined;

export function getAppName(): string {
  return appName;
}

/**
 * Initialize Faro once at app startup. No-op when observability is disabled.
 * Device/session context is attached to our own events (pushEvent/reportFaroError).
 */
export function initFaro(): void {
  if (!enabled || !collectUrl) return;
  try {
    getObservabilityContext();
    faroInstance = initializeFaro({
      url: collectUrl,
      app: {
        name: appName,
        version: appVersion,
        environment,
      },
      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: true,
        }),
      ],
    });
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[Faro] Initialization failed', err);
    }
  }
}

/**
 * Update Faro user context after login. Only stable identifiers; no PII or tokens.
 */
export function setFaroUserContext(meta: FaroUserContext): void {
  if (!enabled) return;
  try {
    const faro = faroInstance ?? getInternalFaroFromGlobalObject();
    if (faro?.api?.setUser) {
      faro.api.setUser({
        id: meta.user_id ?? undefined,
        username: meta.tenant_id ? `tenant:${meta.tenant_id}` : undefined,
      });
    }
  } catch {
    // ignore
  }
}

/**
 * Push a custom event to Faro (e.g. api_error). No-op when disabled.
 */
export function pushFaroEvent(name: string, attributes?: Record<string, string | number | boolean>): void {
  if (!enabled) return;
  try {
    const faro = faroInstance ?? getInternalFaroFromGlobalObject();
    if (faro?.api?.pushEvent && attributes) {
      const stringAttrs: Record<string, string> = {};
      for (const [k, v] of Object.entries(attributes)) {
        stringAttrs[k] = typeof v === 'string' ? v : String(v);
      }
      faro.api.pushEvent(name, stringAttrs);
    }
  } catch {
    // ignore
  }
}

/**
 * Report an error to Faro. No-op when disabled.
 */
export function reportFaroError(error: Error, context?: Record<string, string>): void {
  if (!enabled) return;
  try {
    const faro = faroInstance ?? getInternalFaroFromGlobalObject();
    if (faro?.api?.pushError) {
      faro.api.pushError(error, context);
    }
  } catch {
    // ignore
  }
}

export function isObservabilityEnabled(): boolean {
  return enabled;
}
